import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./FilePreview.module.css";

interface FilePreviewProps {
  file: { name: string; type: string };
  previewUrl: string;
  onRemove?: () => void;
  size?: "default" | "small";
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove, previewUrl, size = "default", }) => {
  const [resolvedType, setResolvedType] = useState(file.type);
  const isImage = resolvedType.startsWith("image/");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setResolvedType(file.type);
    if (file.type !== "application/octet-stream") return;
    fetch(previewUrl, { method: "HEAD" })
      .then((res) => {
        const ct = res.headers.get("Content-Type");
        if (ct) setResolvedType(ct.split(";")[0].trim());
      })
      .catch(() => {});
  }, [previewUrl, file.type]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLightboxOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  const previewBoxClass = `${styles.previewBox} ${size === "small" ? styles.small : ""
    }`;
  return (
    <>
      <div className={previewBoxClass}>
        {onRemove && (
          <button className={styles.removeBtn} onClick={onRemove} aria-label="Remove file" />
        )}
        {isImage ? (
          <img
            src={previewUrl}
            alt={file.name}
            className={styles.image}
            onClick={() => setLightboxOpen(true)}
          />

        ) : (
          <a href={previewUrl} download={file.name} target="_blank" rel="noopener noreferrer" className={styles.pdfIcon} />
        )}
        <a href={previewUrl} download={file.name} target="_blank" rel="noopener noreferrer" className={styles.downloadIcon} aria-label="Download">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>

          <span className={styles.fileName}>{file.name}</span>
</a>
      </div>

      {lightboxOpen && createPortal(
        <div className={styles.lightboxOverlay} onClick={() => setLightboxOpen(false)}>
          <button className={styles.lightboxClose} onClick={() => setLightboxOpen(false)} aria-label="Close">✕</button>
          <img
            src={previewUrl}
            alt={file.name}
            className={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default FilePreview;
